import * as Sentry from "@sentry/vue";

import api from "@/services/utils/apiInstance.ts";
import { displayResponseError } from "@/utils/toastUtils.ts";
import { useAuthStore } from "@/stores/authStore.ts";
import {
  deobfuscatePayload,
  getObfuscationVersion,
  isObfuscationEnabled,
  isObfuscationEnvelope,
  obfuscatePayload,
} from "@/services/utils/obfuscation.ts";

import type {
  ApiError,
  CustomAxiosRequestConfig,
} from "@/types/services/utils/axios.type.ts";
import { isCancel, type AxiosError } from "axios";

const EXCLUDED_ROUTES = [
  "/auth/login",
  "/auth/refresh-token",
  "/auth/logout",
  "/forgot-password",
  "/reset-password",
  "/initialization-password",
];
const INVALID_CSRF_ERROR_KEY = "invalid_csrf_error";
const UNAUTHORIZED_ENTITY_ACCESS_ERROR_KEY = "unauthorized_entity_access_error";

interface QueueEntry {
  resolve: () => void;
  reject: (error: AxiosError) => void;
}

let isRefreshing = false;
let pendingQueue: QueueEntry[] = [];

function processQueue(error: AxiosError | null): void {
  pendingQueue.forEach((entry: QueueEntry) => {
    if (error) {
      entry.reject(error);
    } else {
      entry.resolve();
    }
  });
  pendingQueue = [];
}

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match?.[2] ?? null;
}

function safelyEncodeUrlSegment(segment: string): string {
  if (segment.length === 0) return segment;

  try {
    return encodeURIComponent(decodeURIComponent(segment));
  } catch {
    return encodeURIComponent(segment);
  }
}

function normalizeUrlPath(url?: string): string | undefined {
  if (!url || /^https?:\/\//i.test(url)) return url;

  const [pathname = "", queryString] = url.split("?");
  const encodedPathname = pathname
    .split("/")
    .map((segment: string): string => safelyEncodeUrlSegment(segment))
    .join("/");

  return queryString ? `${encodedPathname}?${queryString}` : encodedPathname;
}

function clearFormDataContentType(
  headers: CustomAxiosRequestConfig["headers"],
): void {
  if (!headers) return;

  if (typeof headers.delete === "function") {
    headers.delete("Content-Type");
    headers.delete("content-type");
    return;
  }

  delete headers["Content-Type"];
  delete headers["content-type"];
}

function getHeader(
  headers: CustomAxiosRequestConfig["headers"],
  name: string,
): string | undefined {
  if (!headers) return undefined;

  if (typeof headers.get === "function") {
    const value = headers.get(name);
    return typeof value === "string" ? value : undefined;
  }

  const value = headers[name] ?? headers[name.toLowerCase()];
  return typeof value === "string" ? value : undefined;
}

function hasObfuscationHeader(
  headers: CustomAxiosRequestConfig["headers"],
): boolean {
  return getHeader(headers, "X-Obfuscation") === getObfuscationVersion();
}

function getResponseErrorKey(
  data: ApiError | string | undefined,
): string | undefined {
  if (typeof data === "string") return data.trim() || undefined;
  return data?.key;
}

const setup = () => {
  const authStore = useAuthStore();

  api.interceptors.request.use((config) => {
    config.url = normalizeUrlPath(config.url);

    const csrf = getCookie("key_account_csrf_token");
    if (csrf) {
      config.headers["X-CSRF-Token"] = csrf;
    }

    if (isObfuscationEnabled()) {
      config.headers["X-Obfuscation-Accept"] = getObfuscationVersion();
    }

    if (
      isObfuscationEnabled() &&
      config.data !== undefined &&
      config.data !== null &&
      !hasObfuscationHeader(config.headers) &&
      !(config.data instanceof FormData)
    ) {
      config.headers["X-Obfuscation"] = getObfuscationVersion();
      config.data = obfuscatePayload(config.data);
    }

    if (config.data instanceof FormData) {
      clearFormDataContentType(config.headers);
    }

    return config;
  });

  api.interceptors.response.use(
    (res) => {
      if (
        isObfuscationEnabled() &&
        res.headers?.["x-obfuscation"] === getObfuscationVersion() &&
        isObfuscationEnvelope(res.data)
      ) {
        res.data = deobfuscatePayload(res.data);
      }

      return res;
    },
    async (error: AxiosError<ApiError>) => {
      if (isCancel(error) || error.name === "AbortError") {
        return Promise.reject(error);
      }

      const { response } = error;
      const config = error.config as CustomAxiosRequestConfig;
      if (
        isObfuscationEnabled() &&
        response?.headers?.["x-obfuscation"] === getObfuscationVersion() &&
        isObfuscationEnvelope(response.data)
      ) {
        response.data = deobfuscatePayload(response.data) as ApiError;
      }
      const errorKey = getResponseErrorKey(
        response?.data as ApiError | string | undefined,
      );

      const isExcluded =
        config.publicCapability ||
        EXCLUDED_ROUTES.some((route) => config.url?.includes(route));
      if (
        !isExcluded &&
        response?.status === 401 &&
        errorKey === INVALID_CSRF_ERROR_KEY
      ) {
        await authStore.logoutAction();
        displayResponseError(error);
        return new Promise<never>(() => {});
      }

      if (
        !isExcluded &&
        response?.status === 401 &&
        errorKey !== INVALID_CSRF_ERROR_KEY &&
        !config._retry
      ) {
        if (isRefreshing) {
          config._retry = true;
          return (
            new Promise<void>((resolve, reject) => {
              pendingQueue.push({ resolve, reject });
            })
              .then(() => api(config))
              // Refresh failed — navigation to /login is handled; swallow to prevent an unhandled rejection in Sentry.
              .catch(() => new Promise<never>(() => {}))
          );
        }

        config._retry = true;
        isRefreshing = true;

        try {
          await api.get("/auth/refresh-token");
          processQueue(null);
          return api(config);
        } catch (refreshError) {
          processQueue(refreshError as AxiosError);
          await authStore.logoutAction();
          displayResponseError(error);
          // Navigation to /login is handled — swallow to prevent an unhandled rejection in Sentry.
          return new Promise<never>(() => {});
        } finally {
          isRefreshing = false;
        }
      }

      if (!isExcluded && response?.status === 401) {
        await authStore.logoutAction();
        displayResponseError(error);
        // Navigation to /login is handled — swallow to prevent an unhandled rejection in Sentry.
        return new Promise<never>(() => {});
      }

      if (
        !isExcluded &&
        response?.status === 403 &&
        (errorKey === UNAUTHORIZED_ENTITY_ACCESS_ERROR_KEY ||
          errorKey === undefined)
      ) {
        await authStore.logoutAction();
        displayResponseError(error);
        // Navigation to /login is handled — swallow to prevent an unhandled rejection in Sentry.
        return new Promise<never>(() => {});
      }

      if (response && response.status >= 500) {
        Sentry.captureException(error, {
          extra: {
            url: config.url,
            method: config.method,
            status: response.status,
            errorKey,
          },
        });
      }

      if (!config.disableGlobalError) {
        displayResponseError(error);
      }

      return Promise.reject(error);
    },
  );
};

export default setup;
